import { useState } from 'react';
import { toastService } from '../../../services/toastService';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import type { PromoteGate } from './brandingLogic';

export default function RolloutPanel({
  deviceType, promoteGate, bootVersions, onDone,
}: {
  deviceType: string; promoteGate: PromoteGate;
  bootVersions: { current?: number; staged?: number; history: number[] };
  onDone: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (label: string, fn: () => Promise<any>, ok: string) => {
    setBusy(label);
    try { await fn(); toastService.success(ok); onDone(); }
    catch (e: any) { toastService.error(e?.message || `${label} failed`); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 space-y-3">
      <div>
        <h2 className="text-sm font-extrabold text-gray-900">Publish &amp; rollout</h2>
        {/*
          THE THREE STATES, SAID OUT LOUD.

          One sentence was not enough, and the cost was real: nobody had ever
          pressed Promote, so `branding-manifest.json` had never been written
          and the fleet carried no branding at all — while the panel showed a
          version chip reading "live". Every device defaults to the STABLE
          channel, so an upload that is only published reaches nothing.
        */}
        <ol className="mt-1.5 space-y-0.5 text-xs text-gray-500">
          <li><b className="text-gray-700">1 Upload</b> — staged. Nothing has left this screen.</li>
          <li><b className="text-amber-700">2 Publish to canary</b> — reaches devices on the <b>canary</b> channel only.</li>
          <li><b className="text-emerald-700">3 Promote to fleet</b> — reaches <b>every</b> device. Until this runs, the fleet has no branding.</li>
        </ol>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <button disabled={!!busy} onClick={() => run('publish', () => deviceConfigApi.publish(deviceType), 'Published to canary')}
          className="px-4 py-2.5 rounded-xl text-sm font-extrabold bg-amber-500 text-amber-950 disabled:opacity-50">⬆ Publish to canary</button>
        <button disabled={!!busy || !promoteGate.ok}
          onClick={() => run('promote', () => deviceConfigApi.promote(deviceType), 'Promoted to fleet')}
          className="px-4 py-2.5 rounded-xl text-sm font-extrabold bg-emerald-500 text-emerald-950 disabled:opacity-50">🚀 Promote to fleet</button>
        {/* An unverified promote is about to send an untested animation to
            every till. That is a decision, not a footnote, so it gets a box
            rather than grey text beside a button. A plain locked reason stays
            quiet — it is explaining a disabled control, not warning anyone. */}
        {promoteGate.reason && (
          promoteGate.unverified ? (
            <span className="max-w-md rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-800">
              ⚠ {promoteGate.reason}
            </span>
          ) : (
            <span className="max-w-md text-[11px] text-gray-400">{promoteGate.reason}</span>
          )
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500 pt-1">
        <b className="text-gray-900">Boot versions:</b>
        {/*
          🔴 "LIVE" WAS A LIE. `slots.boot.version` is what PUBLISH wrote, and
          publish writes the canary manifest only — promote is the step that
          copies it to the fleet. Labelling it "live" made a version that had
          reached nobody look shipped, which is a large part of why the fleet
          manifest was never written at all. Say what it is.
        */}
        {bootVersions.current ? (
          <span
            className="px-2.5 py-1 rounded-full border border-amber-500 text-amber-700"
            title="Published to the canary channel. Promote to fleet is what sends it to every device."
          >
            v{bootVersions.current} on canary
          </span>
        ) : null}
        {bootVersions.staged ? <span className="px-2.5 py-1 rounded-full border border-amber-500 text-amber-600">v{bootVersions.staged} staged</span> : null}
        {bootVersions.history.map((v) => (
          <button key={v} disabled={!!busy}
            onClick={() => run('revert', () => deviceConfigApi.revert(deviceType, 'boot', v), `Reverted boot to v${v}`)}
            className="px-2.5 py-1 rounded-full border border-gray-300 text-gray-600 hover:border-gray-400">v{v} ↺ revert</button>
        ))}
      </div>
    </div>
  );
}
